import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import {
  PhysicsWorld,
  COLLISION_GROUP_AI_VEHICLE,
  COLLISION_GROUP_CHASSIS,
  COLLISION_GROUP_GROUND,
  COLLISION_GROUP_STATIC,
  COLLISION_GROUP_DYNAMIC_PROP,
} from '../core/PhysicsWorld';
import { TrafficLight } from '../environment/TrafficLights';
import { AIPedestrian } from './AIPedestrian';
import { ObstacleVehicle } from './AIVehicle';

export class AIBus {
  public mesh: THREE.Group;
  public position = new THREE.Vector3();
  public speedKmh: number = 30;
  public waypoints: THREE.Vector3[] = [];
  public currentWaypointIdx: number = 0;
  public blinkerState: 'left' | 'right' | 'hazard' | 'off' = 'off';

  public isAtBusStop: boolean = false;
  public busStopWaitTimer: number = 0;
  public body: CANNON.Body | null = null;

  private blinkTimer: number = 0;
  private blinkOn: boolean = false;
  private indicatorL!: THREE.Mesh;
  private indicatorR!: THREE.Mesh;

  constructor(waypointsOrPw?: THREE.Vector3[] | PhysicsWorld, physicsWorld?: PhysicsWorld) {
    if (Array.isArray(waypointsOrPw)) {
      this.waypoints = waypointsOrPw;
    } else {
      this.waypoints = [
        new THREE.Vector3(2.2, 0.4, 110),
        new THREE.Vector3(3.2, 0.4, 35),
        new THREE.Vector3(3.4, 0.4, 15),
        new THREE.Vector3(2.2, 0.4, -10),
        new THREE.Vector3(2.2, 0.4, -60),
        new THREE.Vector3(2.2, 0.4, -110),
      ];
      if (waypointsOrPw && 'world' in (waypointsOrPw as any)) {
        physicsWorld = waypointsOrPw as PhysicsWorld;
      }
    }
    this.currentWaypointIdx = this.waypoints.length > 1 ? 1 : 0;
    this.position.copy(this.waypoints[0] || new THREE.Vector3(2.2, 0.4, 60));

    this.mesh = new THREE.Group();
    this.buildBusMesh();
    this.mesh.position.copy(this.position);

    if (physicsWorld) {
      this.initPhysicsBody(physicsWorld);
    }
  }

  private initPhysicsBody(physicsWorld: PhysicsWorld) {
    const chassisShape = new CANNON.Box(new CANNON.Vec3(1.25, 1.4, 4.8));
    this.body = new CANNON.Body({
      type: CANNON.Body.KINEMATIC,
      material: physicsWorld.aiVehicleMaterial,
      collisionFilterGroup: COLLISION_GROUP_AI_VEHICLE,
      collisionFilterMask:
        COLLISION_GROUP_GROUND |
        COLLISION_GROUP_CHASSIS |
        COLLISION_GROUP_STATIC |
        COLLISION_GROUP_DYNAMIC_PROP,
    });
    this.body.addShape(chassisShape, new CANNON.Vec3(0, 1.4, 0));
    this.body.position.set(this.position.x, this.position.y, this.position.z);
    physicsWorld.world.addBody(this.body);
  }

  private buildBusMesh() {
    // German Transit Bus (Yellow transit livery like BVG/MVG, white roof)
    const busYellowMat = new THREE.MeshStandardMaterial({
      color: 0xeab308, // German post/transit yellow
      roughness: 0.35,
      metalness: 0.2,
    });
    const busWhiteMat = new THREE.MeshStandardMaterial({ color: 0xf8fafc, roughness: 0.4 });
    const skirtMat = new THREE.MeshStandardMaterial({ color: 0x334155, roughness: 0.8 });
    const glassMat = new THREE.MeshStandardMaterial({
      color: 0x0f172a,
      roughness: 0.1,
      metalness: 0.9,
      transparent: true,
      opacity: 0.7,
    });
    const rubberMat = new THREE.MeshStandardMaterial({ color: 0x1e293b, roughness: 0.9 });
    const wheelRimMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.8, roughness: 0.2 });

    // 1. Lower Body & Skirt (Length 9.6m, Width 2.5m, Height 2.8m)
    const body = new THREE.Mesh(new THREE.BoxGeometry(2.5, 1.4, 9.6), busYellowMat);
    body.position.y = 1.1;
    body.castShadow = true;
    body.receiveShadow = true;

    const skirt = new THREE.Mesh(new THREE.BoxGeometry(2.52, 0.4, 9.62), skirtMat);
    skirt.position.y = 0.45;
    skirt.castShadow = true;

    const roof = new THREE.Mesh(new THREE.BoxGeometry(2.48, 0.3, 9.5), busWhiteMat);
    roof.position.y = 2.85;

    // 2. Large Tinted Windows
    const windowSides = new THREE.Mesh(new THREE.BoxGeometry(2.54, 0.95, 8.2), glassMat);
    windowSides.position.y = 2.25;

    const windshield = new THREE.Mesh(new THREE.BoxGeometry(2.4, 1.1, 0.1), glassMat);
    windshield.position.set(0, 2.15, 4.8);

    const rearGlass = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.9, 0.1), glassMat);
    rearGlass.position.set(0, 2.25, -4.8);

    // 3. LED Destination Display Matrix ("102 Stadtzentrum")
    const destCanvas = document.createElement('canvas');
    destCanvas.width = 512;
    destCanvas.height = 128;
    const dctx = destCanvas.getContext('2d');
    if (dctx) {
      dctx.fillStyle = '#0f172a';
      dctx.fillRect(0, 0, 512, 128);
      dctx.fillStyle = '#f97316'; // Amber LED
      dctx.font = 'bold 58px Inter, monospace';
      dctx.textAlign = 'center';
      dctx.textBaseline = 'middle';
      dctx.fillText('102 Stadtzentrum', 256, 64);
    }
    const destTex = new THREE.CanvasTexture(destCanvas);
    const destMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1.8, 0.45),
      new THREE.MeshBasicMaterial({ map: destTex })
    );
    destMesh.position.set(0, 2.85, 4.82);

    this.mesh.add(body, skirt, roof, windowSides, windshield, rearGlass, destMesh);

    // 4. Wheels
    const wheelGeom = new THREE.CylinderGeometry(0.48, 0.48, 0.32, 18);
    const wheelPositions = [
      { x: -1.2, z: 3.0 },
      { x: 1.2, z: 3.0 },
      { x: -1.2, z: -2.8 },
      { x: 1.2, z: -2.8 },
    ];
    wheelPositions.forEach((wp) => {
      const wheel = new THREE.Mesh(wheelGeom, rubberMat);
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(wp.x, 0.48, wp.z);
      wheel.castShadow = true;
      this.mesh.add(wheel);
    });

    // 5. Indicators (Left & Right)
    const indGeom = new THREE.BoxGeometry(0.2, 0.12, 0.08);
    this.indicatorL = new THREE.Mesh(indGeom, new THREE.MeshBasicMaterial({ color: 0x334155 }));
    this.indicatorL.position.set(-1.22, 0.95, 4.82);

    this.indicatorR = new THREE.Mesh(indGeom, new THREE.MeshBasicMaterial({ color: 0x334155 }));
    this.indicatorR.position.set(1.22, 0.95, 4.82);

    this.mesh.add(this.indicatorL, this.indicatorR);
  }

  public update(
    deltaTime: number,
    playerPos?: THREE.Vector3,
    playerSpeedKmh: number = 0,
    trafficLights?: TrafficLight[],
    pedestrians?: AIPedestrian[],
    otherVehicles?: ObstacleVehicle[]
  ) {
    if (this.waypoints.length === 0) return;

    // ── Frame 0 / Severe Overlap Defense ───────────────────────────────────
    if (playerPos && this.position.distanceTo(playerPos) < 1.8) {
      for (let i = 0; i < this.waypoints.length; i++) {
        const nextIdx = (this.currentWaypointIdx + i + 1) % this.waypoints.length;
        if (this.waypoints[nextIdx].distanceTo(playerPos) >= 18.0) {
          this.currentWaypointIdx = nextIdx;
          this.position.copy(this.waypoints[nextIdx]);
          this.mesh.position.copy(this.position);
          if (this.body) {
            this.body.position.set(this.position.x, this.position.y, this.position.z);
            this.body.velocity.set(0, 0, 0);
          }
          break;
        }
      }
    }

    // Blink timer (380ms flash)
    this.blinkTimer += deltaTime;
    if (this.blinkTimer > 0.38) {
      this.blinkTimer = 0;
      this.blinkOn = !this.blinkOn;
    }

    // Update indicator visuals
    const indLMat = this.indicatorL.material as THREE.MeshBasicMaterial;
    const indRMat = this.indicatorR.material as THREE.MeshBasicMaterial;
    const isL = (this.blinkerState === 'left' || this.blinkerState === 'hazard') && this.blinkOn;
    const isR = (this.blinkerState === 'right' || this.blinkerState === 'hazard') && this.blinkOn;
    indLMat.color.setHex(isL ? 0xf59e0b : 0x334155);
    indRMat.color.setHex(isR ? 0xf59e0b : 0x334155);

    // Check bus stop zone at z ~ 15
    const distToBusStop = Math.hypot(this.position.x - 3.6, this.position.z - 15);
    if (!this.isAtBusStop && distToBusStop < 4.0) {
      this.isAtBusStop = true;
      this.busStopWaitTimer = 0;
      this.blinkerState = 'hazard'; // Hazard blinkers while boarding
    }

    if (this.isAtBusStop) {
      this.busStopWaitTimer += deltaTime;
      if (this.busStopWaitTimer < 6.0) {
        // Paused at bus stop
        this.speedKmh = 0;
        if (this.body) {
          this.body.position.set(this.position.x, this.position.y, this.position.z);
          this.body.velocity.set(0, 0, 0);
        }
        return;
      } else if (this.busStopWaitTimer < 8.5) {
        // Signaling departure with LEFT indicator (§ 20 Abs. 5 StVO: Abfahren ermöglichen!)
        this.blinkerState = 'left';
        this.speedKmh = 12;
      } else {
        // Departed, return to cruise
        this.isAtBusStop = false;
        this.blinkerState = 'off';
        this.speedKmh = 35;
      }
    }

    // Follow waypoints
    const targetWp = this.waypoints[this.currentWaypointIdx];
    const toTarget = targetWp.clone().sub(this.position);
    const dist = toTarget.length();

    if (dist < 2.5) {
      this.currentWaypointIdx = (this.currentWaypointIdx + 1) % this.waypoints.length;
      return;
    }

    const dir = toTarget.clone().normalize();

    // ── 0. Vehicle Ahead Queueing Detection ─────────────────────────────────
    let vehicleAheadRequiresStop = false;
    let distToVehicleAhead = 999;
    let speedOfVehicleAhead = 0;
    let busStopBuffer = 7.9; // 4.5m bus half-length + 2.2m car half-length + 1.2m gap

    if (otherVehicles && otherVehicles.length > 0) {
      for (const other of otherVehicles) {
        if (other.entity === this || other.position === this.position) continue;
        const toOther = other.position.clone().sub(this.position);
        const alongDir = toOther.dot(dir);
        const lateral = Math.abs(-dir.z * toOther.x + dir.x * toOther.z);
        if (alongDir > 1.0 && alongDir < 26.0 && lateral < 2.8) {
          const requiredBuffer = 4.5 + (other.halfLength ?? 2.2) + 1.2;
          if (alongDir < distToVehicleAhead) {
            distToVehicleAhead = alongDir;
            speedOfVehicleAhead = other.speedKmh;
            busStopBuffer = requiredBuffer;
            vehicleAheadRequiresStop = true;
          }
        }
      }
    }

    // ── 1. Traffic Light Detection ──────────────────────────────────────────
    let lightRequiresStop = false;
    let distToLight = 999;
    if (trafficLights && trafficLights.length > 0) {
      for (const tl of trafficLights) {
        if (tl.phase === 'RED' || tl.phase === 'RED_YELLOW') {
          const align = dir.dot(tl.approachDirection);
          if (align > 0.7) {
            const toTl = tl.group.position.clone().sub(this.position);
            const alongDir = toTl.dot(dir);
            const lateral = Math.abs(-dir.z * toTl.x + dir.x * toTl.z);
            if (alongDir > 2.0 && alongDir < 26.0 && lateral < 6.0) {
              lightRequiresStop = true;
              distToLight = Math.min(distToLight, alongDir);
            }
          }
        }
      }
    }

    // ── 2. Pedestrian Yielding Detection ───────────────────────────────────
    let pedRequiresStop = false;
    let distToPed = 999;
    if (pedestrians && pedestrians.length > 0) {
      for (const ped of pedestrians) {
        const toPed = ped.position.clone().sub(this.position);
        const alongDir = toPed.dot(dir);
        const lateral = Math.abs(-dir.z * toPed.x + dir.x * toPed.z);
        if (alongDir > 1.5 && alongDir < 20.0 && lateral < 3.2) {
          pedRequiresStop = true;
          distToPed = Math.min(distToPed, alongDir);
        }
      }
    }

    // ── 3. Hard Obstacle Avoidance & TTC Braking with Player ─────────────────
    let dotFront = -1;
    let distToPlayer = 999;
    const SAFE_STOP_DIST = 8.8;
    const DANGER_DIST = 22.0;

    if (playerPos) {
      distToPlayer = this.position.distanceTo(playerPos);
      const toPlayer = playerPos.clone().sub(this.position);
      dotFront = toPlayer.dot(dir);
      const lateralDist = Math.abs(-dir.z * toPlayer.x + dir.x * toPlayer.z);

      const playerInCorridor = dotFront > 0.5 && dotFront < DANGER_DIST && lateralDist < 2.8;
      const isImminentProximity = dotFront > 0.5 && distToPlayer < SAFE_STOP_DIST;

      if (isImminentProximity || playerInCorridor) {
        const closingSpeedKmh = Math.max(1.0, this.speedKmh - playerSpeedKmh);
        const availableDist = Math.max(0, distToPlayer - SAFE_STOP_DIST);
        const ttc = availableDist / (closingSpeedKmh / 3.6);

        if (isImminentProximity || distToPlayer <= SAFE_STOP_DIST || ttc < 2.5) {
          // Full stop: gap > 1.5m guaranteed
          this.speedKmh = 0;
        } else {
          // Smooth progressive braking
          const t = availableDist / (DANGER_DIST - SAFE_STOP_DIST);
          this.speedKmh = Math.max(0, Math.min(this.speedKmh, 35 * t));
        }
      }
    }

    // ── Braking Resolutions ────────────────────────────────────────────────
    if (vehicleAheadRequiresStop) {
      if (distToVehicleAhead <= busStopBuffer) {
        this.speedKmh = 0;
      } else {
        const t = Math.max(0, (distToVehicleAhead - busStopBuffer) / (26.0 - busStopBuffer));
        const matched = Math.min(speedOfVehicleAhead, 35);
        this.speedKmh = Math.min(this.speedKmh, Math.max(matched, 35 * t));
        if (this.speedKmh < 4.0 && speedOfVehicleAhead < 2.0) {
          this.speedKmh = 0;
        }
      }
    }

    if (lightRequiresStop) {
      if (distToLight <= 7.5) {
        this.speedKmh = 0;
      } else {
        const t = Math.max(0, (distToLight - 7.5) / (26.0 - 7.5));
        this.speedKmh = Math.min(this.speedKmh, 35 * t);
        if (this.speedKmh < 4.0) {
          this.speedKmh = 0;
        }
      }
    }

    if (pedRequiresStop) {
      if (distToPed <= 7.0) {
        this.speedKmh = 0;
      } else {
        const t = Math.max(0, (distToPed - 7.0) / (20.0 - 7.0));
        this.speedKmh = Math.min(this.speedKmh, 35 * t);
        if (this.speedKmh < 4.0) {
          this.speedKmh = 0;
        }
      }
    }

    let moveDist = (this.speedKmh / 3.6) * deltaTime;
    // Hard physical clamp: Never penetrate player's hull
    if (playerPos && dotFront > 0.5) {
      const maxAllowed = Math.max(0, distToPlayer - SAFE_STOP_DIST);
      moveDist = Math.min(moveDist, maxAllowed);
    }
    // Hard physical clamp: Never cross traffic light stop line on RED
    if (lightRequiresStop && distToLight <= 6.5) {
      moveDist = 0;
    }
    // Hard physical clamp: Never hit crossing pedestrian
    if (pedRequiresStop && distToPed <= 6.0) {
      moveDist = 0;
    }
    // Hard physical clamp: Never penetrate vehicle ahead
    if (vehicleAheadRequiresStop && distToVehicleAhead <= busStopBuffer - 0.2) {
      moveDist = 0;
    }

    if (moveDist > 0.0001) {
      this.position.add(dir.clone().multiplyScalar(Math.min(moveDist, dist)));
      this.mesh.position.copy(this.position);
    }

    const heading = Math.atan2(dir.x, dir.z);
    this.mesh.rotation.y = heading;

    if (this.body) {
      this.body.position.set(this.position.x, this.position.y, this.position.z);
      const speedMs = this.speedKmh / 3.6;
      this.body.velocity.set(dir.x * speedMs, 0, dir.z * speedMs);
      this.body.quaternion.setFromEuler(0, heading, 0);
    }
  }
}
